import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { NAVIGATION_ITEMS, USER_ROLES, UserRole } from '../helpers/constants';
import { savePostLoginRedirect, clearPostLoginRedirect } from '../utils/auth';
import { isAdminUIDisabled } from '../config/runtimeConfig';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRoles = [] 
}) => {
  const { isAuthenticated, user, isInitializing } = useAuth();
  const location = useLocation();

  // Wait for authentication to finish initializing before making redirect decisions
  if (isInitializing) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // If not authenticated, store the intended location and redirect to login
  if (!isAuthenticated) {
    // Never store /sessionExpired as post-login target (logout from that page would loop back here)
    if (location.pathname !== '/sessionExpired') {
      savePostLoginRedirect(location.pathname + location.search);
    } else {
      // Clear any stale redirect so re-login does not loop back to /sessionExpired
      clearPostLoginRedirect();
    }
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Admin-only gating (admin_UI) always runs in PROD. In DEV it can be turned off locally via
  // VITE_DISABLE_ADMIN_UI=true in .env.local (build-time constant — cannot be overridden
  // from the browser console). Non-ADMIN role requirements are unaffected either way.
  const effectiveRequiredRoles = (isAdminUIDisabled() && requiredRoles.includes(USER_ROLES.ADMIN))
    ? requiredRoles.filter(role => role !== USER_ROLES.ADMIN)
    : requiredRoles;

  // If no specific roles required, allow access
  if (effectiveRequiredRoles.length === 0) {
    return <>{children}</>;
  }

  // Check if user has required role (only check after initialization is complete)
  const hasRequiredRole = user && effectiveRequiredRoles.includes(user.role);

  if (!hasRequiredRole) {
    // Admin-only gate (the whole app, or a sub-route like /adminHub): send non-admins
    // to the "use Gmail/Outlook/Google Chat" page rather than looping back into the app.
    if (effectiveRequiredRoles.includes(USER_ROLES.ADMIN)) {
      return <Navigate to="/adminOnly" replace />;
    }

    // Redirect to first accessible page based on user role
    const accessiblePages = Object.values(NAVIGATION_ITEMS).filter(item =>
      item.roles.includes(user?.role as UserRole || USER_ROLES.USER)
    );

    if (accessiblePages.length > 0) {
      return <Navigate to={accessiblePages[0].path} replace />;
    }

    // If no accessible pages, redirect to support space (accessible to all)
    return <Navigate to="/supportSpace" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute; 